import { Button } from "@arco-design/web-react";
import { X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { derivePublicItinerary } from "../service/itineraryOutputService";
import type { ItineraryEditor } from "../types";
import { ItineraryClientDetail } from "./ItineraryClientDetail";

/** 门票录入时只读展示当前草稿的标题与每日正文，不触发保存或客户输出查询。 */
export function ItineraryQuickPreview({ editor, onClose }: {
  editor: ItineraryEditor;
  onClose: () => void;
}) {
  const itinerary = useMemo(() => derivePublicItinerary(editor), [editor]);
  const previewRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const preview = previewRef.current;
    const editorPage = preview?.closest(".itinerary-editor-page");
    if (!preview || !editorPage) return;
    const ownerDocument = preview.ownerDocument;
    let pointerInPreview = false;
    let blurFrame: number | undefined;
    const isWithinEditing = (target: EventTarget | null) => target instanceof Element && (
      preview.contains(target)
      || (target.matches("[data-itinerary-ticket-input]") && editorPage.contains(target))
    );
    const onPointerDown = (event: PointerEvent) => {
      pointerInPreview = event.target instanceof Node && preview.contains(event.target);
      if (!isWithinEditing(event.target)) onClose();
    };
    const onFocusIn = (event: FocusEvent) => {
      pointerInPreview = false;
      if (!isWithinEditing(event.target)) onClose();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (!isWithinEditing(event.target)) return;
      if (event.relatedTarget) {
        if (!isWithinEditing(event.relatedTarget)) onClose();
        return;
      }
      // 点击正文和原生滚动条会让输入框失焦，仍属于同一次门票对照操作。
      if (blurFrame !== undefined) cancelAnimationFrame(blurFrame);
      blurFrame = requestAnimationFrame(() => {
        if (!pointerInPreview && !isWithinEditing(ownerDocument.activeElement)) onClose();
      });
    };
    const onKeyDown = (event: KeyboardEvent) => {
      pointerInPreview = false;
      if (event.key === "Escape") onClose();
    };
    ownerDocument.addEventListener("pointerdown", onPointerDown, true);
    ownerDocument.addEventListener("focusin", onFocusIn, true);
    ownerDocument.addEventListener("focusout", onFocusOut, true);
    ownerDocument.addEventListener("keydown", onKeyDown, true);
    return () => {
      if (blurFrame !== undefined) cancelAnimationFrame(blurFrame);
      ownerDocument.removeEventListener("pointerdown", onPointerDown, true);
      ownerDocument.removeEventListener("focusin", onFocusIn, true);
      ownerDocument.removeEventListener("focusout", onFocusOut, true);
      ownerDocument.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onClose]);
  return <section ref={previewRef} className="itinerary-customization-panel-content" aria-label="门票编辑行程速览">
    <header className="itinerary-customization-panel-heading">
      <strong>行程速览</strong>
      <Button type="text" size="small" icon={<X size={16} />} aria-label="关闭行程速览" onClick={onClose} />
    </header>
    <div className="itinerary-customization-panel-body is-preview">
      <ItineraryClientDetail itinerary={itinerary} contentOnly idPrefix="itinerary-quick-preview" />
    </div>
  </section>;
}
